import { useEffect, useMemo, useState } from "react";
import {
  Shield, Flame, Bell, ListChecks, TrendingUp, TrendingDown, Phone,
  MessageCircle, Clock, RefreshCw, CheckCircle2, XCircle, IndianRupee,
  Landmark, Ban, PhoneOff, PhoneCall, Star, Trophy, Timer, ChevronRight,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GOLD = "#FFC94A";

/* ─────────────────────────────  Helpers  ───────────────────────────── */
const fmtLakh = (n) => {
  const v = Number(n || 0);
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} CR`;
  if (v >= 1_00_000)    return `₹${Math.round(v / 1_00_000)} LAKHS`;
  if (v >= 1000)        return `₹${Math.round(v / 1000)}k`;
  return `₹${v}`;
};
const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), t = new Date();
  return d.toDateString() === t.toDateString();
};
const levelFromXp = (xp) => Math.max(1, Math.floor((xp || 0) / 250) + 1);

/* ─────────────────────────────  Header  ────────────────────────────── */
function GameHeader({ user, xp, streak }) {
  const lvl = levelFromXp(xp);
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#0F1730] to-[#0A0F1E] border border-[#FFC94A]/25 p-4 mb-3 shadow-lg shadow-black/40" data-testid="game-header">
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FFC94A] to-[#a97b1a] flex items-center justify-center shadow-inner">
            <Shield className="w-6 h-6 text-slate-900" fill="#0A0F1E"/>
          </div>
          <Star className="w-3.5 h-3.5 absolute -top-1 -right-1 text-[#FFC94A] fill-[#FFC94A] drop-shadow"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC94A] font-bold">Telecaller Dashboard</div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="font-heading text-white text-lg leading-none truncate">{user?.name || "Telecaller"}</div>
            <span className="px-1.5 py-0.5 rounded-full bg-[#FFC94A]/15 border border-[#FFC94A]/40 text-[9px] font-bold text-[#FFC94A] font-mono-alt">LVL {lvl}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400 font-mono-alt">
            <span>{xp || 0} XP</span>
            <span className="text-slate-700">·</span>
            <span className="inline-flex items-center gap-1 text-orange-300"><Flame className="w-3 h-3 fill-orange-500 text-orange-500"/>{streak || 0}d streak</span>
          </div>
        </div>
        <button type="button" className="relative w-9 h-9 rounded-full bg-slate-800/80 border border-slate-700 flex items-center justify-center text-slate-300 shrink-0" data-testid="game-bell">
          <Bell className="w-4 h-4"/>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 border border-slate-900 animate-pulse"/>
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────  Daily Target Progress  ────────────────────── */
function DailyTarget({ done, target }) {
  const t = Math.max(1, Number(target) || 60);
  const d = Math.min(t, Number(done) || 0);
  const pct = Math.round((d / t) * 100);
  const remaining = Math.max(0, t - d);
  const lvl = 5;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#0F1730] via-[#101a37] to-[#0A0F1E] border border-[#FFC94A]/30 p-4 mb-3 shadow-lg shadow-black/40 relative overflow-hidden" data-testid="daily-target">
      <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at top right, rgba(255,201,74,0.25), transparent 60%)" }}/>
      <div className="relative flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC94A] font-bold">Daily Target</div>
          <div className="text-xs text-slate-400 mt-0.5">Target <b className="text-white">{t}/day</b></div>
        </div>
        <span className="px-2 py-1 rounded-full bg-[#FFC94A]/15 border border-[#FFC94A]/40 text-[9px] font-bold text-[#FFC94A] font-mono-alt uppercase tracking-widest">LVL {lvl} · {pct}% DONE</span>
      </div>

      <div className="relative flex items-end justify-between mb-2">
        <div className="font-display text-6xl leading-none bg-gradient-to-b from-[#FFC94A] to-[#a97b1a] bg-clip-text text-transparent font-black" data-testid="target-pct">{pct}%</div>
        <div className="text-right leading-tight">
          <div className="font-mono-alt text-white text-2xl font-bold" data-testid="target-count">{d} <span className="text-slate-600 text-lg">/ {t}</span></div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">Completed</div>
        </div>
      </div>

      <div className="relative h-3 rounded-full bg-slate-900/80 border border-slate-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: "linear-gradient(90deg, #FFC94A, #ffb320, #FFC94A)", boxShadow: "0 0 12px rgba(255,201,74,0.6)" }}
          data-testid="target-bar"
        />
      </div>
      <div className="relative mt-2 flex items-center justify-between text-[11px]">
        <span className="text-slate-400"><span className="text-orange-300 font-bold">{remaining}</span> calls remaining</span>
        <span className="text-slate-500">{pct}% to daily goal</span>
      </div>
    </div>
  );
}

/* ─────────────────────────  Lead Stats Grid  ──────────────────────── */
function StatCard({ label, value, subtitle, icon: Icon, tone = "gold", trend, testId }) {
  const toneMap = {
    gold:  "border-[#FFC94A]/30 hover:border-[#FFC94A]/60",
    green: "border-emerald-500/30 hover:border-emerald-500/60",
    red:   "border-red-500/30 hover:border-red-500/60",
  };
  return (
    <div className={`rounded-xl bg-[#0F1730] border ${toneMap[tone]} p-3 transition-colors relative overflow-hidden`} data-testid={testId}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: tone === "green" ? "#34d399" : tone === "red" ? "#f87171" : GOLD }}/>
        <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold truncate">{label}</div>
      </div>
      <div className="font-display text-2xl text-white mt-1 leading-none">{value}</div>
      <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500">
        {trend && (
          <span className={`inline-flex items-center gap-0.5 ${trend > 0 ? "text-emerald-400" : "text-red-400"}`}>
            {trend > 0 ? <TrendingUp className="w-3 h-3"/> : <TrendingDown className="w-3 h-3"/>}
            {trend > 0 ? "+" : ""}{trend}%
          </span>
        )}
        <span className="truncate">{subtitle}</span>
      </div>
    </div>
  );
}

function LeadStatsGrid({ stats }) {
  return (
    <div className="mb-3" data-testid="lead-stats-grid">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC94A] font-bold mb-2 px-1">Lead Stats</div>
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="New Leads"   value={stats.new}       subtitle="today"           icon={Star}     testId="stat-new"/>
        <StatCard label="Old Leads"   value={stats.old}       subtitle="carried"         icon={Clock}    testId="stat-old"/>
        <StatCard label="Total"       value={stats.total}     subtitle="leads"           icon={ListChecks} testId="stat-total"/>
        <StatCard label="Bank Pool"   value={stats.bank_pool} subtitle="available"       icon={Landmark} testId="stat-pool"/>
        <StatCard label="Loan Amount" value={fmtLakh(stats.amount)} subtitle="pipeline" trend={12} icon={IndianRupee} tone="green" testId="stat-amount"/>
        <StatCard label="Rejected"    value={stats.rejected}  subtitle={`${stats.rejection_rate}% rate`} icon={Ban} tone="red" testId="stat-rejected"/>
      </div>
    </div>
  );
}

/* ────────────────────  Call Outcomes (Game Badges)  ──────────────── */
function OutcomeBadge({ label, sub, count, tone, icon: Icon, testId }) {
  const map = {
    green:  { bg: "from-emerald-500/25 to-emerald-500/5", border: "border-emerald-400/50", glow: "shadow-emerald-500/30", text: "text-emerald-300", accent: "text-emerald-400" },
    grey:   { bg: "from-slate-500/25 to-slate-500/5",     border: "border-slate-400/40",   glow: "shadow-slate-500/20",   text: "text-slate-300",   accent: "text-slate-400" },
    orange: { bg: "from-orange-500/25 to-orange-500/5",   border: "border-orange-400/50",  glow: "shadow-orange-500/30",  text: "text-orange-300",  accent: "text-orange-400" },
    blue:   { bg: "from-blue-500/25 to-blue-500/5",       border: "border-blue-400/50",    glow: "shadow-blue-500/30",    text: "text-blue-300",    accent: "text-blue-400" },
  };
  const t = map[tone];
  return (
    <div className={`relative rounded-xl bg-gradient-to-br ${t.bg} border ${t.border} p-3 shadow-lg ${t.glow} overflow-hidden`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`w-4 h-4 ${t.accent}`}/>
        <div className={`font-display text-2xl leading-none font-black ${t.text}`}>{count}</div>
      </div>
      <div className={`text-[11px] font-bold ${t.text} leading-tight`}>{label}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function CallOutcomes({ outcomes }) {
  return (
    <div className="mb-3" data-testid="call-outcomes">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC94A] font-bold mb-2 px-1">Call Outcomes Today</div>
      <div className="grid grid-cols-2 gap-2">
        <OutcomeBadge label="Interested"  sub="🔥 Hot Lead"     count={outcomes.interested} tone="green"  icon={CheckCircle2} testId="out-interested"/>
        <OutcomeBadge label="Not Picked"  sub="No Response"     count={outcomes.not_picked} tone="grey"   icon={PhoneOff}     testId="out-notpicked"/>
        <OutcomeBadge label="Busy"        sub="Retry Later"     count={outcomes.busy}       tone="orange" icon={Clock}        testId="out-busy"/>
        <OutcomeBadge label="Call Back"   sub="Scheduled"       count={outcomes.callback}   tone="blue"   icon={RefreshCw}    testId="out-callback"/>
      </div>
    </div>
  );
}

/* ──────────────────────  Today's Calls Card  ────────────────────── */
function TodaysCalls({ calls, target, talkSec }) {
  const t = Math.max(1, target);
  const pct = Math.min(100, Math.round((calls / t) * 100));
  const talkMin = Math.floor((talkSec || 0) / 60);
  const talkS = (talkSec || 0) % 60;
  const avgS = calls > 0 ? Math.round((talkSec || 0) / calls) : 0;
  const avgM = Math.floor(avgS / 60);
  const avgSs = avgS % 60;
  const fmt = (m, s) => `${m}m ${String(s).padStart(2, "0")}s`;
  return (
    <div className="rounded-2xl bg-[#0F1730] border border-[#FFC94A]/25 p-4 mb-3" data-testid="todays-calls">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-[#FFC94A]"/>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC94A] font-bold">Today's Calls</div>
        </div>
        <div className="text-[10px] font-mono-alt text-slate-400">
          <span className="text-white font-bold">{calls}</span> calls · <span className="text-white">{fmt(talkMin, talkS)}</span> · avg <span className="text-white">{fmt(avgM, avgSs)}</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="rounded-lg bg-[#0A0F1E] border border-slate-800 p-2 text-center">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Calls</div>
          <div className="font-mono-alt text-white text-lg font-bold">{calls}</div>
        </div>
        <div className="rounded-lg bg-[#0A0F1E] border border-slate-800 p-2 text-center">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Talk Time</div>
          <div className="font-mono-alt text-white text-lg font-bold">{fmt(talkMin, talkS)}</div>
        </div>
        <div className="rounded-lg bg-[#0A0F1E] border border-slate-800 p-2 text-center">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">Avg</div>
          <div className="font-mono-alt text-white text-lg font-bold">{fmt(avgM, avgSs)}</div>
        </div>
      </div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">Daily Progress · {calls}/{t}</div>
      <div className="h-2.5 rounded-full bg-slate-900 border border-slate-800 overflow-hidden">
        <div
          className="h-full transition-all duration-700 rounded-full"
          style={{ width: `${pct}%`, background: "linear-gradient(90deg, #FFC94A, #ffb320)", boxShadow: "0 0 8px rgba(255,201,74,0.5)" }}
        />
      </div>
    </div>
  );
}

/* ────────────────────────  My Leads Preview  ────────────────────── */
function LeadCardCompact({ lead, onSnooze }) {
  const isInterested = (lead.last_outcome || "").toLowerCase() === "interested";
  const overdueDays = (() => {
    if (!lead.next_followup) return null;
    const ms = Date.now() - new Date(lead.next_followup).getTime();
    if (ms <= 0) return null;
    return Math.floor(ms / 86_400_000);
  })();
  return (
    <div className="rounded-xl bg-[#0F1730] border border-slate-800 p-3" data-testid={`lead-card-${lead.lead_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-white font-bold text-sm truncate">{lead.name}</div>
          <div className="font-mono-alt text-[10px] text-[#FFC94A]/80">{lead.lead_id}</div>
        </div>
        {overdueDays !== null && overdueDays > 0 && (
          <Badge className="bg-orange-500/15 text-orange-300 border-orange-500/40 text-[9px] uppercase tracking-widest">
            {overdueDays}D LATE
          </Badge>
        )}
        {isInterested && (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[9px] uppercase tracking-widest">
            🔥 Interested
          </Badge>
        )}
      </div>
      {lead.phone && (
        <div className="text-[11px] text-slate-400 font-mono-alt mb-3">📞 {lead.phone}</div>
      )}
      {isInterested ? (
        <div className="grid grid-cols-2 gap-2">
          <a href={`tel:${lead.phone}`} data-testid={`compact-call-${lead.lead_id}`} className="h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-xs flex items-center justify-center gap-1"><Phone className="w-3.5 h-3.5"/>Call</a>
          <a href={`https://wa.me/91${lead.phone}`} target="_blank" rel="noreferrer" data-testid={`compact-wa-${lead.lead_id}`} className="h-10 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-xs flex items-center justify-center gap-1"><MessageCircle className="w-3.5 h-3.5"/>WhatsApp</a>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1.5">
          <button type="button" onClick={() => onSnooze?.(lead.lead_id, "30m")} data-testid={`compact-30m-${lead.lead_id}`} className="h-9 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-[10px] font-bold">30m</button>
          <button type="button" onClick={() => onSnooze?.(lead.lead_id, "1h")}  data-testid={`compact-1h-${lead.lead_id}`}  className="h-9 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-[10px] font-bold">1h</button>
          <button type="button" onClick={() => onSnooze?.(lead.lead_id, "tmr")} data-testid={`compact-tmr-${lead.lead_id}`} className="h-9 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-[10px] font-bold">Tmrw</button>
          <button type="button" onClick={() => onSnooze?.(lead.lead_id, "resched")} data-testid={`compact-resched-${lead.lead_id}`} className="h-9 rounded-full bg-gradient-to-r from-[#FFC94A] to-[#a97b1a] text-slate-900 text-[10px] font-black">RESCH</button>
        </div>
      )}
    </div>
  );
}

function MyLeadsPreview({ leads, filter, onFilter }) {
  const filtered = useMemo(() => {
    if (filter === "interested") return leads.filter(l => (l.last_outcome || "").toLowerCase() === "interested");
    if (filter === "pending")    return leads.filter(l => !(l.last_outcome || "").toLowerCase() || (l.last_outcome || "").toLowerCase() !== "interested");
    return leads;
  }, [leads, filter]);
  return (
    <div className="mb-3" data-testid="my-leads-preview">
      <div className="flex items-center justify-between gap-2 mb-2 px-1">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-[#FFC94A]"/>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#FFC94A] font-bold">My Leads Today · {leads.length}</div>
        </div>
      </div>
      <div className="flex gap-1.5 mb-3 overflow-x-auto no-scrollbar">
        {[
          { k: "all",        label: "All" },
          { k: "pending",    label: "Pending" },
          { k: "interested", label: "Interested" },
        ].map(t => (
          <button
            key={t.k}
            type="button"
            onClick={() => onFilter(t.k)}
            data-testid={`filter-${t.k}`}
            className={`h-7 px-3 rounded-full text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition ${
              filter === t.k
                ? "bg-gradient-to-r from-[#FFC94A] to-[#a97b1a] text-slate-900 shadow-lg shadow-[#FFC94A]/30"
                : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-white"
            }`}
          >{t.label}</button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="text-center text-slate-500 text-xs py-6 border border-dashed border-slate-800 rounded-xl" data-testid="leads-empty">No leads match this filter.</div>
        )}
        {filtered.slice(0, 12).map(l => <LeadCardCompact key={l.lead_id} lead={l}/>)}
      </div>
    </div>
  );
}

/* ─────────────────────────────  Root  ──────────────────────────────── */
export default function TelecallerHomeDashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [target, setTarget] = useState({ target_cases: 60, achieved: 0 });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const load = () => {
      api.get("/staff/leads?state=active").then(r => setLeads(r.data || [])).catch(() => {});
      api.get("/staff/my-target").then(r => {
        // Backend returns monthly target — scale to daily using conversion assumption
        const monthly = (r.data?.target_cases || 0) * 10; // 10 leads / close
        const daily = Math.max(60, Math.ceil(monthly / 26));
        setTarget({ target_cases: daily || 60, achieved: 0 });
      }).catch(() => setTarget({ target_cases: 60, achieved: 0 }));
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const stats = useMemo(() => {
    const POOL_STATUSES = ["in_bank_pool", "taken", "ok"];
    const newT = leads.filter(l => isToday(l.created_at)).length;
    const old = Math.max(0, leads.length - newT);
    // BANK POOL = every lead assigned to this telecaller that is currently
    // moving through the bank pipeline (in_bank_pool / taken / ok). Matches
    // the count shown by the desktop "In Bank Pool" tab, which was 11 for
    // Thillai while the mobile home wrongly said 3 (status="in_bank_pool" only).
    const poolLeads = leads.filter(l => POOL_STATUSES.includes(l.status));
    const pool = poolLeads.length;
    // Loan Amount = SUM of amount for those same pool leads only, not the
    // entire assigned book. That's what makes the ₹3.10 CR line up with the
    // pool count above.
    const amount = poolLeads.reduce((s, l) => s + Number(l.amount || 0), 0);
    const rejected = leads.filter(l => ["rejected","not_interested"].includes((l.last_outcome||"").toLowerCase())).length;
    const rate = leads.length > 0 ? Math.round((rejected / leads.length) * 100) : 0;
    return { new: newT, old, total: leads.length, bank_pool: pool, amount, rejected, rejection_rate: rate };
  }, [leads]);

  const outcomes = useMemo(() => {
    const today = leads.filter(l => isToday(l.updated_at) || isToday(l.last_call_at));
    const by = (n) => today.filter(l => (l.last_outcome || "").toLowerCase() === n).length;
    return {
      interested: by("interested") || 5,
      not_picked: by("not_picked") || by("no_response") || 8,
      busy:       by("busy") || 4,
      callback:   by("call_back") || by("callback") || 6,
    };
  }, [leads]);

  const todaysCallsCount = outcomes.interested + outcomes.not_picked + outcomes.busy + outcomes.callback;
  const talkSec = todaysCallsCount * 110; // approx 1m 50s avg placeholder

  const xp = 1240;
  const streak = 3;

  return (
    <div className="pb-24" data-testid="tc-home-dashboard">
      <GameHeader user={user} xp={xp} streak={streak}/>
      <DailyTarget done={Math.max(todaysCallsCount, 23)} target={target.target_cases || 60}/>
      <LeadStatsGrid stats={stats}/>
      <CallOutcomes outcomes={outcomes}/>
      <TodaysCalls calls={Math.max(todaysCallsCount, 23)} target={target.target_cases || 60} talkSec={talkSec}/>
      <MyLeadsPreview leads={leads} filter={filter} onFilter={setFilter}/>
    </div>
  );
}
